<template>
  <el-dialog
    class="dialog-wrapper roles-modal"
    :visible="true"
    :show-close="false"
    @close="abortAction"
  >
    <ContentHeader
      slot="title"
      :title="
        isModalEdit
          ? $t('operations.update.updateOperation')
          : $t('operations.create.createOperation')
      "
    />
    <ValidationObserver
      ref="operationForm"
      v-slot="{ handleSubmit, invalid, dirty }"
    >
      <el-form
        ref="form"
        label-position="top"
      >
        <el-row
          :gutter="20"
          type="flex"
        >
          <el-col :span="4">
            <FormItem
              rules="required|numeric|min:0"
              :label="$t('operations.create.sequence')"
            >
              <el-input
                v-model.number="formData.sequence"
                type="number"
                min="0"
              />
            </FormItem>
          </el-col>
          <el-col :span="8">
            <FormItem
              rules="required"
              :label="$t('operations.create.operation')"
            >
              <el-input v-model="formData.operation" />
            </FormItem>
          </el-col>
          <el-col :span="8">
            <FormItem :label="$t('operations.create.containerType')">
              <el-input v-model="formData.containerType" />
            </FormItem>
          </el-col>
          <el-col :span="4">
            <FormItem
              rules="required|numeric|min:0"
              :label="$t('operations.create.containerCount')"
            >
              <el-input
                v-model.number="formData.containerCount"
                type="number"
                min="0"
              />
            </FormItem>
          </el-col>
        </el-row>

        <el-row
          :gutter="20"
          type="flex"
        >
          <el-col :span="6">
            <FormItem :label="$t('operations.create.expectedValue')">
              <el-input v-model="formData.expectedValue" />
            </FormItem>
          </el-col>
          <el-col :span="6">
            <FormItem :label="$t('operations.create.measure')">
              <el-autocomplete
                v-model="selectedMeasure"
                style="width: 100%"
                :fetch-suggestions="measuresList"
                clearable
                value-key="label"
                placeholder=""
                @select="
                  (item) => {
                    formData.measureId = item.value;
                  }
                "
                @change="
                  () => {
                    formData.measureId = null;
                  }
                "
              >
                <template slot-scope="{ item }">
                  <AutocompleteItem :item="item" />
                </template>
              </el-autocomplete>
            </FormItem>
          </el-col>
          <el-col :span="6">
            <FormItem :label="$t('operations.create.symbol')">
              <AutocompleteSymbol v-model="formData.symbol" />
            </FormItem>
          </el-col>
          <el-col :span="6">
            <FormItem :label="$t('operations.create.symbolType')">
              <el-select
                v-model="formData.symbolType"
                placeholder="Select"
                clearable
              >
                <el-option
                  v-for="item in symbolTypes"
                  :key="item.value"
                  :label="item.label"
                  :value="item.value"
                >
                </el-option>
              </el-select>
            </FormItem>
          </el-col>
        </el-row>

        <el-row
          :gutter="20"
          type="flex"
        >
          <el-col :span="6">
            <FormItem :label="$t('operations.create.LD')">
              <el-input
                v-model="formData.LD"
                type="text"
              />
            </FormItem>
          </el-col>
          <el-col :span="6">
            <FormItem :label="$t('operations.create.LQ')">
              <el-input
                v-model="formData.LQ"
                type="text"
              />
            </FormItem>
          </el-col>
          <el-col :span="6">
            <FormItem :label="$t('operations.create.U')">
              <el-input
                v-model="formData.U"
                type="text"
              />
            </FormItem>
          </el-col>
          <el-col :span="6">
            <FormItem
              :rules="{
                regex: /^\d+([.,]\d+)?$/,
              }"
              :label="$t('operations.create.recoveryFactor')"
            >
              <el-input
                v-model="formData.recoveryFactor"
                type="text"
              /> </FormItem
          ></el-col>
        </el-row>

        <FormSpecifications
          v-if="!isModalEdit"
          :form-value="formSpecificationData"
          :measure="selectedMeasure"
        />

        <el-row
          :gutter="20"
          type="flex"
        >
          <el-col :span="9">
            <FormItem>
              <el-checkbox v-model="formData.printOnWorksheet">
                {{ $t('operations.create.printOnWorksheet') }}
              </el-checkbox>
            </FormItem>
          </el-col>
          <el-col :span="7">
            <FormItem>
              <el-checkbox v-model="formData.printOnLabels">
                {{ $t('operations.create.printOnLabels') }}
              </el-checkbox>
            </FormItem>
          </el-col>
          <el-col :span="7">
            <FormItem>
              <el-checkbox v-model="formData.mainResult">
                {{ $t('operations.create.mainResult') }}
              </el-checkbox>
            </FormItem>
          </el-col>
        </el-row>

        <FormButtons>
          <el-button
            type="secondary"
            @click.prevent="abortAction"
          >
            {{ $t('operations.create.abortAction') }}
          </el-button>
          <el-button
            v-if="isModalEdit"
            type="primary"
            native-type="submit"
            :disabled="invalid || !dirty"
            @click.prevent="handleSubmit(patchOperation)"
          >
            {{ $t('operations.update.updateOperation') }}
          </el-button>
          <el-button
            v-else
            type="primary"
            native-type="submit"
            :disabled="invalid || !dirty"
            @click.prevent="handleSubmit(createOperation)"
          >
            {{ $t('operations.create.createOperation') }}
          </el-button>
        </FormButtons>
      </el-form>
    </ValidationObserver>
  </el-dialog>
</template>

<script>
  import { mapGetters } from 'vuex';
  import pickBy from 'lodash.pickby';

  import { analysesApi, measuresApi } from '@/api';
  import FormItem from '@/components/FormItem.vue';
  import ContentHeader from '@/components/ContentHeader.vue';
  import FormButtons from '@/components/FormButtons.vue';
  import AutocompleteItem from '@/components/AutocompleteItem.vue';
  import AutocompleteSymbol from '@/components/AutocompleteSymbol.vue';
  import FormSpecifications from '@/components/FormSpecifications.vue';
  import { SymbolTypes } from '@/config/constants';

  const defaultData = {
    sequence: null,
    operation: '',
    containerType: '',
    containerCount: null,
    expectedValue: null,
    measureId: null,
    symbol: '',
    symbolType: '',
    LD: '',
    LQ: '',
    U: '',
    recoveryFactor: null,
    printOnWorksheet: true,
    printOnLabels: true,
    mainResult: true,
  };

  const specificationDefaultData = {
    type: 'None',
    selectionValues: [],
    decimalDigits: null,
    minRange: null,
    maxRange: null,
    isMinRangeInclusive: null,
    isMaxRangeInclusive: null,
    specificationValue: null,
  };

  export default {
    name: 'CreateEditOperation',
    components: {
      FormItem,
      ContentHeader,
      FormButtons,
      AutocompleteItem,
      AutocompleteSymbol,
      FormSpecifications,
    },
    props: {
      visible: { type: Boolean, default: false },
      isModalEdit: { type: Boolean, default: false },
      initialData: { type: Object, default: () => defaultData },
    },
    data() {
      return {
        formData: { ...defaultData },
        selectedMeasure: null,
        formSpecificationData: { ...specificationDefaultData },
        analysisLimitFormatRadio: 'None',
        value: '',
      };
    },
    computed: {
      ...mapGetters(['forms']),
      symbolTypes() {
        return Object.values(SymbolTypes).map((val) => ({
          value: val,
          label: this.$t(`enums.symbolTypes.${val}`),
        }));
      },
    },
    created() {
      if (
        this.initialData.operation &&
        Object.keys(this.initialData.operation).length > 0
      ) {
        const { measure, ...operation } = this.initialData.operation;
        this.formData = operation;
        this.selectedMeasure = measure ? measure.description : '';
      }
      if (
        this.initialData.specification &&
        this.initialData.specification.length > 0
      ) {
        const [specification] = this.initialData.specification;
        this.formSpecificationData = specification;
        this.analysisLimitFormatRadio = specification.type;
      } else {
        this.analysisLimitFormatRadio = 'None';
      }
    },
    methods: {
      checkFieldsByLimitType(limitType, payload) {
        let formattedPayload = {};
        switch (limitType) {
          case 'None': {
            formattedPayload = {};
            break;
          }
          case 'FreeText': {
            formattedPayload = {
              specificationValue: payload.specificationValue,
            };
            break;
          }
          case 'Selection': {
            formattedPayload = {
              selectionValues: payload.selectionValues,
              specificationValue: payload.specificationValue,
            };
            break;
          }
          case 'Range': {
            formattedPayload = {
              decimalDigits: payload.decimalDigits,
              minRange: payload.minRange,
              maxRange: payload.maxRange,
              isMinRangeInclusive: payload.isMinRangeInclusive,
              isMaxRangeInclusive: payload.isMaxRangeInclusive,
            };
            break;
          }
          default:
            formattedPayload = payload;
            break;
        }
        formattedPayload.type = payload.type;
        return formattedPayload;
      },
      async upsertSpecification(operationId) {
        const specificationFormData = this.formSpecificationData;
        const payload = this.checkFieldsByLimitType(
          specificationFormData.type,
          specificationFormData
        );
        const analysisId = this.forms.operations.analysisId || null;
        const specificationId = this.formSpecificationData.id || null;
        try {
          if (specificationId) {
            await analysesApi.specificationPatch(
              analysisId,
              operationId,
              payload,
              specificationId
            );
          } else {
            await analysesApi.specificationCreate(
              analysisId,
              operationId,
              payload
            );
          }
          this.$notify({
            showClose: true,
            type: 'success',
            message: this.$t('specifications.create.success'),
          });
          this.formSpecificationData = { ...specificationDefaultData };
        } catch (error) {
          this.$notify({
            showClose: true,
            type: 'error',
            message: error.response.data.error.message,
          });
        }
      },
      async createOperation() {
        const formData = this.buildFormToSend();
        const analysisId = this.forms.operations.analysisId || null;
        try {
          const operation = await analysesApi.operationsCreate(
            analysisId,
            formData
          );
          this.$notify({
            showClose: true,
            type: 'success',
            message: this.$t('operations.create.success'),
          });
          await this.upsertSpecification(operation.data.id);
          this.formData = { ...defaultData };
          this.$refs.operationForm.reset();
          this.$emit('dialogClose', { success: true });
        } catch (error) {
          this.$notify({
            showClose: true,
            type: 'error',
            message: error.response.data.error.message,
          });
        }
      },
      async patchOperation() {
        const formData = this.buildFormToSend();
        const analysisId = this.forms.operations.analysisId || null;
        try {
          await analysesApi.operationsPut(analysisId, formData.id, formData);
          this.$notify({
            showClose: true,
            type: 'success',
            message: this.$t('operations.update.success'),
          });
          this.formData = { ...defaultData };
          this.$refs.operationForm.reset();
          this.$emit('dialogClose', { success: true });
        } catch (error) {
          this.$notify({
            showClose: true,
            type: 'error',
            message: error.response.data.error.message,
          });
        }
      },
      parseDecimal(value) {
        if (value == null || value === '') return null;
        return parseFloat(String(value).replace(',', '.'));
      },
      buildFormToSend() {
        const payload = {
          ...this.formData,
          recoveryFactor: this.parseDecimal(this.formData.recoveryFactor),
        };
        return pickBy(payload, (x) => x !== null);
      },
      abortAction() {
        this.formData = { ...defaultData };
        this.formSpecificationData = { ...specificationDefaultData };
        this.selectedMeasure = null;
        this.$refs.operationForm.reset();
        this.analysisLimitFormatRadio = 'None';
        this.$emit('dialogClose', { success: false });
      },
      async measuresList(value, cb) {
        const response = await measuresApi.autocompleteFetch(value);
        const measures = response.data.map((item) => ({
          label: item.description,
          value: item.id,
        }));
        cb(measures);
      },
    },
  };
</script>

<style lang="scss">
  .el-radio-group {
    width: 100%;
  }

  .add-btn {
    margin-bottom: 20px;
  }

  .el-icon-error {
    color: red;

    &:hover {
      cursor: pointer;
    }
  }

  .el-dialog {
    .el-form-item {
      margin-bottom: 0;
    }
  }

  .range-input {
    display: flex;

    .el-radio {
      margin-top: 2rem;
      margin-right: 8px;

      &__label {
        display: none;
      }
    }

    label {
      margin-right: 5px;
    }
  }

  .centered-col {
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .spec-value {
    display: flex;

    .el-input {
      margin-right: 10px;
    }
  }
</style>
